import { Flex, PrimaryButton, Typography } from '@banhala/ui-kit';
import type { Address } from 'mypage/src/api/addressService';

type Props = {
  address: Address;
  /** 목록 내 정렬된 인덱스 — QA 자동화 testID(index)용 */
  index: number;
  isSelected: boolean;
  onPress: () => void;
  onPressEdit: () => void;
  onPressDelete: () => void;
};

/**
 * 배송지 목록 셀 — iOS `AddressListCell.swift` 스펙 정확 매칭.
 *
 * - 외곽: padding 24(all), cornerRadius 4, border 1
 * - 외곽 border color: 선택=blue30, 미선택=gray30
 * - 이름(h4) + 기본배송지 배지 row (gap small, alignItems center)
 * - 배지: cornerRadius 11, border 1 blue40, padding h:8/v:4, subtitle4 (isSelected만)
 * - 주소/상세주소/연락처: body2
 * - 이름 color: 선택=content_primary(gray70), 미선택=content_tertiary(gray40)
 * - 주소/상세/연락처 color: 선택=content_primary, 미선택=content_secondary(gray50)
 * - 항목 간 spacing small(8)
 * - 연락처 → 액션 row 사이 setCustomSpacing(16) → margin top 8 추가 (8+8=16)
 * - 액션 row: spacing small(8), fillEqually
 * - 액션 버튼: PrimaryButton outline gray medium, 순서 = 삭제 → 수정
 *
 * 주의:
 * - `borderColor`(RawColorKeys union) vs `borderColorValue`(RAW hex string) 구분
 * - `gap`은 `SpacingSize` literal만 — 'xxsmall'(2)/'xsmall'(4)/'small'(8)/'medium'(12)/'large'(16)/'xlarge'(20)/'xxlarge'(24)
 */
export const AddressItem = ({
  address,
  index,
  isSelected,
  onPress,
  onPressEdit,
  onPressDelete,
}: Props) => {
  return (
    <Flex
      testID={`배송지카드?index=${index}&value=${address.name}`}
      onPress={onPress}
      padding={{ horizontal: 24, vertical: 24 }}
      borderRadius={4}
      borderWidth={1}
      borderColor={isSelected ? 'blue30' : 'gray30'}
      backgroundColor="bg_primary"
      gap={8}
    >
      <Flex.Row alignItems="flex-start" gap={12}>
        <Typography
          type="h4"
          color={isSelected ? 'content_primary' : 'content_tertiary'}
        >
          {address.name}
        </Typography>
        {isSelected && (
          <Flex
            testID="기본배송지뱃지"
            padding={{ horizontal: 8, vertical: 4 }}
            borderRadius={11}
            borderWidth={1}
            borderColor="blue40"
          >
            <Typography type="subtitle4" color="content_blue_primary">
              기본배송지
            </Typography>
          </Flex>
        )}
      </Flex.Row>
      <Typography
        type="body2"
        color={isSelected ? 'content_primary' : 'content_secondary'}
      >
        {address.address}
      </Typography>
      <Typography
        type="body2"
        color={isSelected ? 'content_primary' : 'content_secondary'}
      >
        {address.address_detail}
      </Typography>
      <Typography
        type="body2"
        color={isSelected ? 'content_primary' : 'content_secondary'}
      >
        {address.mobile}
      </Typography>

      {/*
        iOS `setCustomSpacing(16, after: phoneLabel)` 대응.
        외곽 gap이 small(8)이라 추가 margin 8만 더해 총 16 확보.
      */}
      <Flex.Row margin={{ top: 8 }} gap={8}>
        <PrimaryButton
          testID={`배송지카드삭제버튼?index=${index}`}
          variant="hug"
          outline
          color="gray"
          size="medium"
          onPress={onPressDelete}
        >
          삭제
        </PrimaryButton>
        <PrimaryButton
          testID={`배송지카드수정버튼?index=${index}`}
          variant="hug"
          outline
          color="gray"
          size="medium"
          onPress={onPressEdit}
        >
          수정
        </PrimaryButton>
      </Flex.Row>
    </Flex>
  );
};
